import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { Table } from '@/app/entities/TableCredit'
import { contentModalStepsFGTS } from './components/Installments/components/contentModalStepsFGTS'
import { selfHireService } from '@/app/services/selfHireService'
import { useParams } from 'react-router-dom'
import { useToast } from '@/view/components/ui/use-toast'
import { leadsService } from '@/app/services/leadsService'
import { useLeadContext } from '@/app/contexts/LeadContext'
import { isValid, parse } from 'date-fns'
import { getProductsConfig } from '@/app/services/configService/getProductsConfig'

const schema = z.object({
  name: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'Nome é obrigatório'),
  phone: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'Telefone é obrigatório'),
  cpf: z
    .string({ required_error: 'Obrigatório' })
    .min(1, 'CPF é obrigatório')
    .refine((value) => {
      const cpf = value.replace(/\D/g, '')
      return cpf.length === 11
    }, 'CPF deve conter 11 dígitos')
    .transform((value) => value.replace(/\D/g, '')),
  birthDate: z
    .string({ required_error: 'Obrigatório' })
    .min(10, 'A Data é obrigatória')
    .refine((value) => {
      return isValid(parse(value, 'dd/MM/yyyy', new Date()))
    }, 'A Data é inválida'),
})

export type FormCreditData = z.infer<typeof schema>

type getPublicCreditParams = {
  cpf: string
  phone?: string
  birthDate: string
  linkName: string
  prazo?: number
  leadId?: string
}

export const useCreditController = () => {
  const { linkName } = useParams()
  const [result, setResult] = useState<Table | null>(null)
  const [formData, setFormData] = useState<Partial<FormCreditData> | null>(null)
  const [showAuthorizeModal, setShowAuthorizeModal] = useState(false)
  const [showStepsModal, setShowStepsModal] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [showClientForm, setShowClientForm] = useState(false)
  const { toast } = useToast()

  const { setLeadId } = useLeadContext()

  useEffect(() => {
    if (linkName) {
      const timer = setTimeout(async () => {
        try {
          await selfHireService.increaseEntryCount({ name: linkName })
        } catch (error) {
          console.error('Erro ao aumentar contador de entrada:', error)
        }
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [linkName])

  const methods = useForm<FormCreditData>({
    resolver: zodResolver(schema),
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: getPublicCreditParams) =>
      selfHireService.publicCredit(data.cpf, {
        birthDate: data.birthDate,
        linkName: linkName || '',
        prazo: data.prazo || undefined,
        leadId: data.leadId,
      }),
  })

  const parsedBirthDate = (str: string) => {
    return parse(str, 'dd/MM/yyyy', new Date())
  }

  const handleSubmit = async (data: FormCreditData) => {
    try {
      const lead = await leadsService.createLead({
        name: data.name,
        cpf: data.cpf,
        phone: data.phone.replace(/\D/g, ''),
        birthDate: parsedBirthDate(data.birthDate).toISOString(),
        linkName: linkName || '',
      })

      setLeadId(lead._id)

      const productsConfig = await getProductsConfig()
      if (!productsConfig.FGTS) {
        throw new Error('FGTS desativado')
      }

      const response = await mutateAsync({
        cpf: data.cpf,
        birthDate: parsedBirthDate(data.birthDate).toISOString(),
        linkName: linkName || '',
        leadId: lead._id,
      })

      const validTable = response.tables.find(
        (table) => table.success === true && table.ValorLiquidoCliente > 0,
      )

      setFormData({
        cpf: data.cpf,
        name: data.name,
        birthDate: data.birthDate,
      })
      if (validTable) {
        setResult(validTable)
      } else {
        throw new Error(
          response?.tables?.[0]?.message ||
            'Nenhuma tabela valida foi encotrada',
        )
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setResult({
        success: false,
        IDTabela: 0,
        CodigoTabela: '',
        ValorLiquidoCliente: 0,
      } as Table)

      if (error?.message.indexOf('autoriz') >= 0) setShowAuthorizeModal(true)
      else {
        toast({
          variant: 'destructive',
          title:
            error?.message ||
            'Erro ao carregar os dados, tente novamente mais tarde!',
        })
        backToInitial()
      }
    }
  }

  const backToInitial = () => {
    setResult(null)
    setFormData(null)
    setShowAuthorizeModal(false)
    setShowStepsModal(false)
  }

  const handleSimulateInstallment = async (prazo?: number) => {
    if (!formData?.cpf || !formData?.birthDate) return

    try {
      const response = await mutateAsync({
        cpf: formData.cpf,
        birthDate: parsedBirthDate(formData.birthDate).toISOString(),
        linkName: linkName || '',
        prazo,
      })

      const validTable = response.tables.find(
        (table) => table.success === true && table.ValorLiquidoCliente > 0,
      )

      if (validTable) {
        setResult(validTable)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleAuthorizeAdvance = () => {
    setShowAuthorizeModal(false)
    setShowStepsModal(true)
    setCurrentStep(0)
  }

  const handleStepsNext = () => {
    if (currentStep < contentModalStepsFGTS.steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      setShowStepsModal(false)
      setCurrentStep(0)
      if (methods.getValues()) {
        const data = methods.getValues()
        handleSubmit(data)
      }
    }
  }

  const handleStepsPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepsClose = () => {
    setShowStepsModal(false)
    setCurrentStep(0)
  }

  const handleRequestCredit = () => {
    setShowClientForm(true)
  }

  const handleBackToInstallments = () => {
    setShowClientForm(false)
  }

  return {
    methods,
    isPending,
    result,
    formData,
    showAuthorizeModal,
    backToInitial,
    showStepsModal,
    currentStep,
    showClientForm,
    linkName,
    handleSubmit,
    handleSimulateInstallment,
    handleAuthorizeAdvance,
    handleStepsNext,
    handleStepsPrev,
    handleStepsClose,
    handleRequestCredit,
    handleBackToInstallments,
  }
}
